import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  Input,
  OnInit,
  inject,
  signal,
  computed,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Timestamp } from '@angular/fire/firestore';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';
import { ConversationListService } from '../../../../core/src/lib/state/conversation-list.service';
import { ConversationService } from '../../../../core/src/lib/state/conversation.service';
import { isMobile } from '../../../../core/src/lib/inbox-utils';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import { ConversationDetail } from '../../../../core/src/lib/interface/conversation.interface';
import { ACCOUNT_GROUP_ID_TOKEN, CONVERSATION_ROUTES_TOKEN, PARTNER_ID_TOKEN } from '../../../../core/src/lib/tokens';
import { ViewService } from '../../../../core/src/lib/view.service';
import { inboxViewsOptions, InboxView } from '../../../../core/src/lib/conversation-filters';

dayjs.extend(utc);

const currentViewId = () => {
  const route = inject(ActivatedRoute);
  return route.params.pipe(
    map((params) => params.viewId),
    distinctUntilChanged(),
  );
};

@Component({
  selector: 'inbox-pane',
  templateUrl: './inbox-pane.component.html',
  styleUrls: ['./inbox-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InboxPaneComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly conversationListService = inject(ConversationListService);
  protected readonly showInboxViews$ = this.inboxService.showInboxViews$;
  protected readonly showInboxEmpty$ = this.inboxService.showInboxEmpty$;
  protected readonly $loadingError = signal<Error | null>(null);

  readonly formGroup = new FormGroup({
    search: new FormControl('', { nonNullable: true }),
    view: new FormControl<InboxView>(this.getInitialView(), { nonNullable: true }),
  });
  protected readonly featureFlagFullScreen$ = this.inboxService.featureFlagFullscreenInboxPC$;

  readonly inboxViewsTranslations: { [key: string]: string } = {
    none: 'INBOX.FILTERS.INBOX',
    closed: 'INBOX.FILTERS.CLOSED',
    anonymous: 'INBOX.FILTERS.ANONYMOUS',
    private: 'INBOX.FILTERS.PRIVATE',
  };

  filteredConversations$ = this.conversationListService.conversations$.pipe(
    tap(() => {
      //refresh "now"
      this.now = dayjs.utc();
      this.$loadingError.set(null);
    }),
    catchError((err) => {
      console.error('error loading conversations', err);
      this.$loadingError.set(err);
      return of([]);
    }),
  );

  routerState = '';
  currentConversationId = '';
  displayBadge$: Observable<boolean> = this.conversationService.displayBadge$;

  loadingConversations$ = this.conversationListService.loadingConversations$;
  emptyConversations$ = this.conversationListService.emptyConversations$;
  hasClosedConversations$ = this.conversationListService.organizationHasClosedConversations$;

  now = dayjs.utc();

  viewId$ = currentViewId();

  // TODO: @ardydedase combine inputs into a single interface.
  @Input() learnMoreUrl = '';
  @Input() showSearchBar = true;
  @Input() emptyTitleMessage = 'INBOX.EMPTY.TITLE';
  @Input() emptySubtitleMessage = 'INBOX.EMPTY.SMS_SUBTITLE';
  @Input() conversationPath?: string;

  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));

  readonly showFilters$ = this.viewService.selectedViewId$.pipe(map((viewId) => viewId === 'all'));
  readonly showToolbar$ = this.showFilters$.pipe(map((showFilters) => showFilters || this.showSearchBar));
  readonly isBusinessApp = this.inboxService.isBusinessApp;
  canAccessSMS$ = this.inboxService.canAccessSMS$;
  canAccessPrivateChat$ = this.inboxService.canAccessPrivateChat$;

  // TODO(BREW-575): We're going to combine the concepts of 'views' and 'filters'
  // into a single concept, using the dropdwon UI instead of tabs across the
  // top. But for now, we have to handle two different forms of empty view: one
  // for apps like PCC with 'views' tabs, and one for apps like BCC with the
  // 'filters' dropdown. This will get simpler when we consolidate those.
  showEmptyView$ = combineLatest([this.showInboxViews$, this.emptyConversations$]).pipe(
    map(([showInboxViews, emptyConversations]) => !!(showInboxViews && emptyConversations)),
  );

  private readonly $showFilters = toSignal(this.showFilters$, { initialValue: false });
  private readonly $emptyConversations = toSignal(this.emptyConversations$.pipe(catchError(() => of(false))), {
    initialValue: false,
  });
  private readonly $hasClosedConversations = toSignal(this.hasClosedConversations$.pipe(catchError(() => of(false))), {
    initialValue: false,
  });

  protected readonly $showInboxZero = computed(() => {
    const showFilters = this.$showFilters();
    const emptyConversations = this.$emptyConversations();
    const hasClosedConversations = this.$hasClosedConversations();
    const unfiltered = this.formGroup.controls.view.value === 'none';
    return !!(showFilters && unfiltered && emptyConversations && hasClosedConversations);
  });

  constructor(
    private conversationService: ConversationService,
    private inboxService: InboxService,
    @Inject(ACCOUNT_GROUP_ID_TOKEN) readonly accountGroupId$: Observable<string>,
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
    private router: Router,
    private readonly viewService: ViewService,
  ) {}

  ngOnInit(): void {
    this.activeRoute.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.conversationListService.updateFilters({
        isAnonymous: params.get('view') === 'anonymous',
        isOpen: params.get('view') !== 'closed',
        isPrivate: params.get('view') === 'private',
      });
    });

    const currentUrlParts = this.router.url.split('/');
    this.routerState = currentUrlParts[currentUrlParts.length - 1];
    if (!isMobile() && this.routerState === 'conversations') {
      this.router.navigate([`${this.routes()?.root}/inbox/home`]);
    }

    this.formGroup.controls.search.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500), startWith(''), distinctUntilChanged())
      .subscribe((term) => this.conversationListService.setFilterTerm(term));

    this.formGroup.controls.view.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filter) => {
      const queryParams: Params = { view: filter ? filter : null };
      this.router.navigate([], {
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
    });
  }

  conversationSelected(selected: boolean): void {
    this.conversationService.conversationSelected(selected);
  }

  withinPreviousDayFirestoreTimestamp(timestamp: Timestamp): boolean | undefined {
    if (timestamp) {
      return this.withinPreviousDay(timestamp.toDate());
    }
  }

  withinPreviousDay(date: Date): boolean | undefined {
    if (date) {
      return this.now.diff(dayjs(date), 'hour') <= 24;
    }
  }

  trackConversationDetailsById(i: number, conversationDetail: ConversationDetail): string | undefined {
    return conversationDetail?.conversation?.conversationId;
  }

  loadMoreConversations(): void {
    this.conversationListService.loadMoreConversations();
  }

  getInitialView(): InboxView {
    const viewParam = this.activeRoute.snapshot.queryParamMap.get('view') as InboxView;
    if (inboxViewsOptions.includes(viewParam)) {
      return viewParam;
    }
    return 'none';
  }
}
